<script setup lang="ts">
import { ref, watch, computed, nextTick, onMounted, onBeforeUnmount } from 'vue'
import { useI18n } from 'vue-i18n'
import { useChatStore } from '@/stores/useChatStore'
import { useSettingsStore } from '@/stores/useSettingsStore'
import { useAuthStore } from '@/stores/useAuthStore'
import { useSpeech } from '@/composables/useSpeech'

const { t } = useI18n()
const chat = useChatStore()
const auth = useAuthStore()

const input = ref('')
const inputEl = ref<HTMLTextAreaElement | null>(null)
const listEl = ref<HTMLElement | null>(null)
// Root of the dock — we publish its collapsed height on :root so
// the WebcamPanel (and content-wrapper padding) can anchor itself
// directly above this without guessing the dock's size.
const dockEl = ref<HTMLElement | null>(null)

// Speech-to-text: prefer the Expo shell's native bridge (sets
// window.__witalNativeMic = true in its injectedJavaScriptBeforeContentLoaded),
// fall back to the browser's SpeechRecognition API (webkit prefix on
// Safari + Chrome; Firefox still lacks it as of 2025). On an iOS WKWebView
// without the shell bridge the constructor is either missing or silently
// non-functional, so we'd rather hide the mic than expose a dead button.
const hasNativeMicBridge =
  typeof window !== 'undefined' && (window as any).__witalNativeMic === true
const SpeechRecognitionCtor: any =
  (typeof window !== 'undefined' &&
    ((window as any).SpeechRecognition || (window as any).webkitSpeechRecognition)) ||
  null
// BCP-47 locales the device's speech recognizer actually supports,
// surfaced by the Expo shell at boot (buildInjectJs in mobile/app/index.jsx).
// Empty → either we're on web/non-shell, or the platform API returned
// nothing (Android ≤ 12). In both cases we show the mic optimistically
// and let the start() call surface any error.
const SUPPORTED_MIC_LOCALES: string[] =
  (typeof window !== 'undefined' && (window as any).__witalSupportedMicLocales) || []

function normalizeToBcp47(code: string): string {
  if (!code) return ''
  if (code.includes('-')) {
    const [base, region] = code.split('-')
    return `${base.toLowerCase()}-${region.toUpperCase()}`
  }
  return code.toLowerCase()
}

// Supported-locale lookup: exact match first, then base-language match so
// "en" finds "en-US", "zh" finds "zh-CN", etc. Unknown list → optimistic.
function nativeMicSupportsLang(lang: string): boolean {
  if (!SUPPORTED_MIC_LOCALES.length) return true
  const norm = normalizeToBcp47(lang)
  if (SUPPORTED_MIC_LOCALES.includes(norm)) return true
  const base = norm.split('-')[0]
  return SUPPORTED_MIC_LOCALES.some((l) => l === base || l.startsWith(base + '-'))
}

const micAvailable = computed(() => {
  if (hasNativeMicBridge) {
    return nativeMicSupportsLang(useSettingsStore().language || 'en')
  }
  return !!SpeechRecognitionCtor
})
const ttsAvailable = computed(
  () => typeof window !== 'undefined' && !!window.speechSynthesis,
)
const isListening = ref(false)
// Set by finalizeTranscript (the mic path) and cleared by send/chip
// clicks. A new assistant reply while the flag is true triggers auto-
// TTS so voice-in becomes voice-in → voice-out without the user having
// to tap the speaker icon on the bubble.
const lastInputWasVoice = ref(false)
let recognition: any = null

function micLang(): string {
  try {
    const settings = useSettingsStore()
    if (settings.language) return settings.language
  } catch {}
  return 'en-US'
}

function finalizeTranscript(text: string) {
  const finalText = text.trim()
  if (!finalText) return
  input.value = ''
  lastInputWasVoice.value = true
  chat.sendMessage(finalText)
}

function toggleMic() {
  if (hasNativeMicBridge) return toggleNativeMic()
  return toggleBrowserMic()
}

function toggleNativeMic() {
  const rn = (window as any).ReactNativeWebView
  if (!rn) return
  if (isListening.value) {
    rn.postMessage(JSON.stringify({ type: 'mic:stop' }))
    return
  }
  // Optimistically flip the UI to "listening" so the button reflects
  // intent immediately; the shell's 'start' event will confirm, and
  // 'error' / 'end' will reset if the permission prompt is denied.
  isListening.value = true
  rn.postMessage(JSON.stringify({ type: 'mic:start', lang: micLang() }))
}

function onWitalMicEvent(ev: Event) {
  const d = (ev as CustomEvent).detail
  if (!d || typeof d !== 'object') return
  if (d.type === 'start') {
    isListening.value = true
  } else if (d.type === 'result') {
    if (typeof d.transcript === 'string' && d.transcript) {
      input.value = d.transcript
    }
    if (d.isFinal) finalizeTranscript(input.value)
  } else if (d.type === 'end') {
    isListening.value = false
  } else if (d.type === 'error') {
    isListening.value = false
    // Surface native speech-recognition errors inline so users can act on
    // permission denials or unsupported locales. We cap display time so a
    // rapid re-tap doesn't stack messages.
    const msg = (typeof d.message === 'string' && d.message)
      ? d.message
      : (typeof d.error === 'string' && d.error)
        ? `Speech recognition error: ${d.error}`
        : 'Speech recognition failed.'
    micError.value = msg
    if (micErrorTimer) clearTimeout(micErrorTimer)
    micErrorTimer = window.setTimeout(() => { micError.value = '' }, 6000)
  }
}

const micError = ref('')
let micErrorTimer: number | null = null

function toggleBrowserMic() {
  if (!SpeechRecognitionCtor) return
  if (isListening.value && recognition) {
    try { recognition.stop() } catch {}
    return
  }
  try {
    recognition = new SpeechRecognitionCtor()
    recognition.lang = micLang()
    recognition.interimResults = true
    recognition.continuous = false
    recognition.onstart = () => { isListening.value = true }
    recognition.onend = () => { isListening.value = false }
    recognition.onerror = () => { isListening.value = false }
    recognition.onresult = (ev: any) => {
      let transcript = ''
      for (let i = ev.resultIndex; i < ev.results.length; i++) {
        transcript += ev.results[i][0].transcript
      }
      if (transcript) input.value = transcript
      // When the result is final, submit automatically for a hands-free
      // feel. Users who want to review can tap the mic mid-sentence to
      // stop, edit, and send manually.
      if (ev.results[ev.results.length - 1].isFinal) {
        finalizeTranscript(input.value)
      }
    }
    recognition.start()
  } catch (e) {
    isListening.value = false
  }
}

const canSend = computed(() => !!input.value.trim() && !chat.isLoading)

/** Cycling placeholder examples — the pill rotates through these while
 *  the input is empty AND there are no messages yet. Stops as soon as
 *  the user starts typing or a conversation begins, so it doesn't
 *  distract during active use. */
const EXAMPLES_I18N_KEYS = [
  'chat.examples.rainTomorrow',
  'chat.examples.jogNow',
  'chat.examples.picnicParis',
  'chat.examples.umbrellaToday',
] as const
const exampleIdx = ref(0)
let exampleTimer: ReturnType<typeof setInterval> | null = null

const rotatingPlaceholder = computed(() =>
  t(EXAMPLES_I18N_KEYS[exampleIdx.value % EXAMPLES_I18N_KEYS.length]),
)

const shouldRotate = computed(() =>
  !input.value && !chat.hasMessages && !chat.isLoading,
)

function startRotation() {
  if (exampleTimer) return
  exampleTimer = setInterval(() => {
    if (shouldRotate.value) exampleIdx.value++
  }, 3500)
}

function stopRotation() {
  if (exampleTimer) { clearInterval(exampleTimer); exampleTimer = null }
}

// Publish the dock's collapsed height to :root so surrounding layout
// (WebcamPanel bottom offset, content-wrapper padding) can sit
// immediately above it. We freeze the value while the chat history
// is expanded — the expanding dock is expected to overlay the webcam,
// so we don't want the webcam following the dock upward.
let dockRO: ResizeObserver | null = null
function isChatExpanded() {
  return chat.sheetVisible && chat.hasMessages
}
function publishDockHeight() {
  const el = dockEl.value
  if (!el) return
  if (isChatExpanded()) return
  document.documentElement.style.setProperty('--chat-dock-height', `${el.offsetHeight}px`)
}

onMounted(() => {
  // Start on a random example so two visits don't feel identical.
  exampleIdx.value = Math.floor(Math.random() * EXAMPLES_I18N_KEYS.length)
  startRotation()
  startVisualViewportWatcher()
  if (dockEl.value) {
    dockRO = new ResizeObserver(publishDockHeight)
    dockRO.observe(dockEl.value)
  }
  publishDockHeight()
  if (hasNativeMicBridge) {
    window.addEventListener('witalmic', onWitalMicEvent)
  }
})

// When the chat history closes, re-measure so the webcam anchors to
// the freshly collapsed dock (rate hint may have toggled in the
// meantime, too).
watch(
  () => isChatExpanded(),
  (expanded) => {
    if (!expanded) nextTick(publishDockHeight)
  },
)

onBeforeUnmount(() => {
  stopRotation()
  stopVisualViewportWatcher()
  dockRO?.disconnect()
  dockRO = null
  if (hasNativeMicBridge) {
    window.removeEventListener('witalmic', onWitalMicEvent)
    // If we're unmounting mid-listen, tell the shell to release the mic.
    if (isListening.value) {
      (window as any).ReactNativeWebView?.postMessage(
        JSON.stringify({ type: 'mic:stop' }),
      )
    }
  }
})

/** iOS Safari (and WebKit on iOS Chrome / Firefox) positions
 *  `position: fixed` elements against the *layout* viewport, not the
 *  visual viewport. When the soft keyboard opens, the layout viewport
 *  doesn't shrink — so our chat dock stays pinned to the page bottom
 *  and gets hidden behind the keyboard. The VisualViewport API
 *  exposes the true visible area; we compute how many pixels the
 *  keyboard is covering and push the dock up by that much via a CSS
 *  custom property. Android and desktop browsers don't have this
 *  issue (layout === visual) so the offset stays 0 and nothing moves.
 *  #36. */
let vvResizeHandler: (() => void) | null = null
let vvScrollHandler: (() => void) | null = null

function startVisualViewportWatcher() {
  const vv = typeof window !== 'undefined' ? window.visualViewport : null
  if (!vv) return
  const update = () => {
    const offset = Math.max(0, window.innerHeight - vv.height - vv.offsetTop)
    document.documentElement.style.setProperty('--chat-bottom-offset', `${offset}px`)
  }
  vvResizeHandler = update
  vvScrollHandler = update
  vv.addEventListener('resize', update)
  vv.addEventListener('scroll', update)
  update()
}

function stopVisualViewportWatcher() {
  const vv = typeof window !== 'undefined' ? window.visualViewport : null
  if (!vv) return
  if (vvResizeHandler) vv.removeEventListener('resize', vvResizeHandler)
  if (vvScrollHandler) vv.removeEventListener('scroll', vvScrollHandler)
  vvResizeHandler = null
  vvScrollHandler = null
  // Leave the CSS var at 0 so a later re-mount starts clean.
  document.documentElement.style.setProperty('--chat-bottom-offset', '0px')
}

/** Split bubble text around http(s) URLs so we can render them as
 *  <a> tags while leaving everything else as plain text. Keeps things
 *  safe: no Markdown, no HTML injection, only real URLs become links. */
type BubblePart = { type: 'text' | 'link'; value: string }
const URL_RE = /(https?:\/\/[^\s<>()]+[^\s<>().,;:!?'"])/g

function renderBubble(text: string): BubblePart[] {
  if (!text) return [{ type: 'text', value: '' }]
  const parts: BubblePart[] = []
  let last = 0
  for (const match of text.matchAll(URL_RE)) {
    const idx = match.index ?? 0
    if (idx > last) parts.push({ type: 'text', value: text.slice(last, idx) })
    parts.push({ type: 'link', value: match[0] })
    last = idx + match[0].length
  }
  if (last < text.length) parts.push({ type: 'text', value: text.slice(last) })
  return parts.length ? parts : [{ type: 'text', value: text }]
}

/** Per-bubble TTS. useSpeech already handles voice picking, iOS voice
 *  loading, and cancel-on-language-change — we just wire each assistant
 *  bubble to its own instance keyed by bubble id. */
const ttsSpeakingId = ref<number | null>(null)
const ttsContent = ref('')
const tts = useSpeech(
  () => ttsContent.value,
  () => (useSettingsStore().language || 'en'),
)

function speakBubble(messageId: number, text: string) {
  // Toggle: tap again to stop the currently-speaking bubble.
  if (ttsSpeakingId.value === messageId && tts.isReading.value) {
    try { window.speechSynthesis?.cancel() } catch {}
    ttsSpeakingId.value = null
    return
  }
  // Stop anything else that might be speaking before we start.
  try { window.speechSynthesis?.cancel() } catch {}
  ttsContent.value = text
  ttsSpeakingId.value = messageId
  tts.readContent()
}

// useSpeech flips isReading to false when the utterance ends or errors.
// Clear our per-bubble id so the icon returns to the 'speak' state.
watch(() => tts.isReading.value, (reading) => {
  if (!reading) ttsSpeakingId.value = null
})

// Voice-in → voice-out: when a new assistant bubble arrives and the
// preceding send was triggered by the mic, auto-speak the reply.
// Errors don't auto-speak — a spoken rate-limit message would add
// noise, not value. The flag is one-shot per turn: consumed on the
// assistant reply or cleared on a non-voice send.
watch(() => chat.messages.length, (count, prev) => {
  if (count <= prev) return
  const last = chat.messages[count - 1]
  if (!last) return
  if (last.role === 'assistant' && lastInputWasVoice.value && last.text && ttsAvailable.value) {
    speakBubble(last.id, last.text)
  }
  if (last.role === 'assistant' || last.role === 'error') {
    lastInputWasVoice.value = false
  }
})

const copyState = ref<'idle' | 'done' | 'error'>('idle')
let copyResetTimer: ReturnType<typeof setTimeout> | null = null

async function copyConversation() {
  const lines = chat.messages
    .filter((m) => m.role === 'user' || m.role === 'assistant')
    .map((m) => {
      const tag = m.role === 'user' ? 'You' : 'WITAL'
      return `${tag}: ${m.text}`
    })
  const text = lines.join('\n\n')
  if (!text) return
  try {
    await navigator.clipboard.writeText(text)
    copyState.value = 'done'
  } catch {
    // Fallback for Safari-without-clipboard-API or blocked contexts:
    // create a hidden textarea and use execCommand. Best-effort.
    try {
      const ta = document.createElement('textarea')
      ta.value = text
      ta.style.position = 'fixed'
      ta.style.opacity = '0'
      document.body.appendChild(ta)
      ta.select()
      document.execCommand('copy')
      document.body.removeChild(ta)
      copyState.value = 'done'
    } catch {
      copyState.value = 'error'
    }
  }
  if (copyResetTimer) clearTimeout(copyResetTimer)
  copyResetTimer = setTimeout(() => { copyState.value = 'idle' }, 1800)
}

onBeforeUnmount(() => { if (copyResetTimer) clearTimeout(copyResetTimer) })

const scrollToBottom = async () => {
  await nextTick()
  if (listEl.value) listEl.value.scrollTop = listEl.value.scrollHeight
}

watch(() => chat.messages.length, scrollToBottom)
watch(() => chat.isLoading, scrollToBottom)

const send = async () => {
  const text = input.value.trim()
  if (!text || chat.isLoading) return
  input.value = ''
  lastInputWasVoice.value = false
  autoResize()
  await chat.sendMessage(text)
  inputEl.value?.focus()
}

/** Follow-up suggestions arrive on assistant bubbles as
 *  metadata.suggestions: string[]; ambiguous locations arrive as
 *  metadata.alternatives: string[]. Per the chat UI budget we show at
 *  most 2 chips total: 1 alt (when alternatives present) + 1 follow-up,
 *  else up to 2 follow-ups. */
const MAX_CHIP_CHARS = 60
const MAX_CHIPS_PER_BUBBLE = 2

interface ChatMessageShape {
  id: number
  role: string
  text: string
  metadata?: Record<string, unknown>
}

function getRawSuggestions(m: ChatMessageShape): string[] {
  if (m.role !== 'assistant') return []
  const raw = m.metadata?.suggestions
  if (!Array.isArray(raw)) return []
  return raw.filter((s): s is string => typeof s === 'string' && s.trim().length > 0)
}

function getFirstAlternative(m: ChatMessageShape): string | null {
  if (m.role !== 'assistant') return null
  const raw = m.metadata?.alternatives
  if (!Array.isArray(raw)) return null
  const first = raw.find((s): s is string => typeof s === 'string' && s.trim().length > 0)
  return first ?? null
}

function getSuggestions(m: ChatMessageShape): string[] {
  const suggestions = getRawSuggestions(m)
  const followUpSlots = getFirstAlternative(m) ? MAX_CHIPS_PER_BUBBLE - 1 : MAX_CHIPS_PER_BUBBLE
  return suggestions.slice(0, followUpSlots)
}

/** Turn a canonical alternative string like "Bristol, TN, US" into a
 *  first-person clarification chip label: "I meant the Bristol in TN, US".
 *  Falls back to "I meant {alt}" for single-token strings.
 *  Positional placeholders ({0}/{1}) rather than named ({city}/{rest})
 *  because AWS Translate in the app's translation pipeline rewrites
 *  named placeholder tokens into the target language ("{city}" →
 *  "{城市}", "{rest}" → "{休息}") and then vue-i18n can't substitute
 *  them. Numeric placeholders pass through untouched. */
function formatAltChipLabel(alt: string): string {
  const firstComma = alt.indexOf(',')
  if (firstComma === -1) {
    return t('chat.altMeantChipBare', [alt.trim()])
  }
  const city = alt.slice(0, firstComma).trim()
  const rest = alt.slice(firstComma + 1).trim()
  if (!rest) return t('chat.altMeantChipBare', [city])
  return t('chat.altMeantChip', [city, rest])
}

function truncateChip(s: string): string {
  return s.length > MAX_CHIP_CHARS ? s.slice(0, MAX_CHIP_CHARS - 1).trimEnd() + '…' : s
}

/** Chip taps auto-submit — both follow-up and alt chips behave as if
 *  the user typed the text and pressed Enter. Intentional override of
 *  the original #33 prefill design. */
function useSuggestion(s: string) {
  if (chat.isLoading) return
  input.value = ''
  lastInputWasVoice.value = false
  autoResize()
  chat.sendMessage(s)
}

/** Scan back from this assistant bubble for the user turn it was
 *  replying to. Used when tapping the alt chip — we re-send that same
 *  user text with a location override. */
function findPrecedingUserText(assistantId: number): string {
  const msgs = chat.messages
  const idx = msgs.findIndex((m) => m.id === assistantId)
  if (idx < 0) return ''
  for (let i = idx - 1; i >= 0; i--) {
    if (msgs[i].role === 'user') return msgs[i].text
  }
  return ''
}

function useAlternative(m: ChatMessageShape) {
  if (chat.isLoading) return
  const alt = getFirstAlternative(m)
  if (!alt) return
  const userText = findPrecedingUserText(m.id)
  if (!userText) return
  lastInputWasVoice.value = false
  chat.sendMessage(userText, { locationOverride: alt })
}

/** Resolve the display text for an error bubble. Rate-limit errors
 *  come through with an empty `text` and a `metadata.kind` tag so the
 *  copy can be auth-aware and localised; other errors carry the
 *  server detail directly in `text` as a fallback. */
function errorText(m: { text: string; metadata?: Record<string, unknown> }): string {
  const kind = m.metadata?.kind
  if (kind === 'rate_limit_anon') return t('chat.rateLimitAnon')
  if (kind === 'rate_limit_auth') return t('chat.rateLimitAuth')
  return m.text
}

function hasSignInCta(m: { role: string; metadata?: Record<string, unknown> }): boolean {
  return m.role === 'error' && m.metadata?.action === 'signIn'
}

const onKey = (e: KeyboardEvent) => {
  if (e.key === 'Enter' && !e.shiftKey) {
    e.preventDefault()
    send()
  }
}

// Auto-grow the textarea up to ~4 lines; prevents the pill from
// jumping oddly while letting the user see multi-line queries.
const autoResize = () => {
  const el = inputEl.value
  if (!el) return
  el.style.height = 'auto'
  const cap = 120
  el.style.height = Math.min(el.scrollHeight, cap) + 'px'
}

watch(input, () => autoResize())
</script>

<template>
  <div ref="dockEl" class="chat-dock" :class="{ 'chat-dock--with-history': chat.sheetVisible && chat.hasMessages }">
    <!-- Conversation history. Shown when the user has at least one
         message AND the sheet hasn't been explicitly dismissed. Icons
         are semantically paired: × (left) ends the session and wipes
         history; − (right) just minimises the sheet without wiping,
         so sending another message re-opens it. Copy sits in the
         centre. The row is spread left/centre/right so fat-finger
         taps don't misfire between adjacent actions. -->
    <template v-if="chat.sheetVisible && chat.hasMessages">
      <div class="chat-dock__actions">
        <button
          type="button"
          class="chat-dock__action-btn"
          :aria-label="t('chat.newConversation')"
          :title="t('chat.newConversation')"
          @click="chat.clear()"
        >
          <v-icon icon="mdi-close" size="16" />
        </button>
        <button
          type="button"
          class="chat-dock__action-btn"
          :aria-label="copyState === 'done' ? t('chat.copied') : t('chat.copy')"
          :title="copyState === 'done' ? t('chat.copied') : t('chat.copy')"
          @click="copyConversation"
        >
          <v-icon :icon="copyState === 'done' ? 'mdi-check' : 'mdi-content-copy'" size="16" />
        </button>
        <button
          type="button"
          class="chat-dock__action-btn"
          :aria-label="t('chat.close')"
          :title="t('chat.close')"
          @click="chat.dismiss()"
        >
          <v-icon icon="mdi-minus" size="16" />
        </button>
      </div>
      <div ref="listEl" class="chat-dock__history" role="log" aria-live="polite">
        <template v-for="m in chat.messages" :key="m.id">
          <div
            class="chat-bubble"
            :class="{
              'chat-bubble--user': m.role === 'user',
              'chat-bubble--assistant': m.role === 'assistant',
              'chat-bubble--error': m.role === 'error',
            }"
          >
            <div class="chat-bubble__text">
              <template v-if="m.role === 'error'">{{ errorText(m) }}</template>
              <template v-else>
                <template v-for="(part, idx) in renderBubble(m.text)" :key="idx">
                  <a
                    v-if="part.type === 'link'"
                    :href="part.value"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="chat-bubble__link"
                  >{{ part.value }}</a>
                  <span v-else>{{ part.value }}</span>
                </template>
              </template>
            </div>
            <!-- Speaker icon only on assistant replies and only if the
                 browser exposes speech synthesis. Error bubbles (redirects,
                 out-of-range) and user echoes don't get it. -->
            <button
              v-if="m.role === 'assistant' && ttsAvailable"
              type="button"
              class="chat-bubble__tts"
              :class="{ 'chat-bubble__tts--on': ttsSpeakingId === m.id }"
              :aria-label="ttsSpeakingId === m.id ? t('chat.ttsStop') : t('chat.ttsPlay')"
              :title="ttsSpeakingId === m.id ? t('chat.ttsStop') : t('chat.ttsPlay')"
              @click="speakBubble(m.id, m.text)"
            >
              <v-icon :icon="ttsSpeakingId === m.id ? 'mdi-stop' : 'mdi-volume-high'" size="16" />
            </button>
          </div>
          <div
            v-if="getFirstAlternative(m) || getSuggestions(m).length"
            class="chat-suggestions"
            :aria-label="t('chat.suggestionsAriaLabel')"
          >
            <button
              v-if="getFirstAlternative(m)"
              type="button"
              class="chat-suggestion-chip chat-suggestion-chip--alt"
              :title="formatAltChipLabel(getFirstAlternative(m)!)"
              :disabled="chat.isLoading"
              @click="useAlternative(m)"
            >{{ truncateChip(formatAltChipLabel(getFirstAlternative(m)!)) }}</button>
            <button
              v-for="(s, i) in getSuggestions(m)"
              :key="i"
              type="button"
              class="chat-suggestion-chip"
              :title="s"
              :disabled="chat.isLoading"
              @click="useSuggestion(s)"
            >{{ truncateChip(s) }}</button>
          </div>
          <div v-if="hasSignInCta(m)" class="chat-cta-row">
            <button
              type="button"
              class="chat-cta-btn"
              @click="auth.requestAuthDialog()"
            >
              <v-icon icon="mdi-login" size="16" />
              <span>{{ t('chat.signInCta') }}</span>
            </button>
          </div>
        </template>
        <div v-if="chat.isLoading" class="chat-bubble chat-bubble--assistant chat-bubble--typing">
          <v-progress-circular indeterminate size="14" width="2" />
          <span>{{ t('chat.typing') }}</span>
        </div>
      </div>
    </template>

    <div
      v-if="typeof chat.rateRemaining === 'number' && chat.rateRemaining <= 2"
      class="chat-dock__rate"
    >
      {{ t('chat.rateHint', { remaining: chat.rateRemaining }) }}
    </div>

    <div v-if="micError" class="chat-dock__mic-error" role="alert">
      {{ micError }}
    </div>

    <div class="chat-dock__pill" :class="{ 'chat-dock__pill--loading': chat.isLoading }">
      <textarea
        ref="inputEl"
        v-model="input"
        rows="1"
        class="chat-dock__input"
        :placeholder="rotatingPlaceholder"
        :disabled="chat.isLoading"
        :aria-label="t('chat.inputAriaLabel')"
        @keydown="onKey"
      />
      <button
        v-if="micAvailable"
        type="button"
        class="chat-dock__mic-btn"
        :class="{ 'chat-dock__mic-btn--on': isListening }"
        :disabled="chat.isLoading"
        :aria-label="isListening ? t('chat.micStop') : t('chat.micStart')"
        :title="isListening ? t('chat.micStop') : t('chat.micStart')"
        @click="toggleMic"
      >
        <v-icon :icon="isListening ? 'mdi-microphone' : 'mdi-microphone-outline'" size="18" />
      </button>
      <button
        type="button"
        class="chat-dock__send-btn"
        :disabled="!canSend"
        :aria-label="t('chat.send')"
        @click="send"
      >
        <v-icon icon="mdi-send" size="16" />
      </button>
    </div>
  </div>
</template>

<style scoped>
.chat-dock {
  position: fixed;
  left: 50%;
  transform: translateX(-50%);
  /* iOS Safari keyboard push-up: #36. When the soft keyboard is
   * open, VisualViewport tracking sets --chat-bottom-offset to the
   * keyboard height so the dock sits just above it. 0 on Android
   * and desktop, so no behaviour change there. */
  bottom: var(--chat-bottom-offset, 0px);
  width: 100%;
  max-width: var(--app-column-max-width, 640px);
  padding: 8px 12px;
  padding-bottom: calc(8px + env(safe-area-inset-bottom));
  background: rgb(var(--v-theme-surface));
  border-top: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  box-shadow: 0 -6px 18px rgba(0, 0, 0, 0.1);
  z-index: 8;
  display: flex;
  flex-direction: column;
  gap: 8px;
  /* Dock stays anchored at the bottom and shrinks to fit its content.
   * Only when many messages accumulate does the cap kick in, stopping
   * the history just short of the sticky app header. AppHeader's
   * ResizeObserver publishes --app-header-height; the fallback covers
   * first paint and the case where the header isn't mounted. Uses dvh
   * so mobile URL-bar resizes don't leave a visible gap. */
  max-height: calc(100dvh - var(--app-header-height, 56px) - 8px);
}

/* Inside the Expo WebView shell the native AdBanner sits below the
 * WebView and owns the home-indicator safe area, so the dock should
 * not reserve its own inset or an ~34px / ~24px band shows up between
 * the pill and the ad (iOS) or between the webcam and the pill (Android).
 * `.in-app` is added to <html> by the shell's INJECT_JS; PWA path is
 * untouched. */
.in-app .chat-dock {
  padding-bottom: 8px;
}

/* Action row — end-session / copy / minimise spread left/centre/right
   so adjacent taps don't misfire. Sits on its own row above the
   bubble list so it doesn't steal horizontal space from messages. */
.chat-dock__actions {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 4px;
}

.chat-dock__action-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  padding: 0;
  border: 0;
  border-radius: 50%;
  background: rgba(var(--v-theme-on-surface), 0.12);
  color: rgb(var(--v-theme-on-surface));
  cursor: pointer;
  transition: background-color 120ms ease;
}

.chat-dock__action-btn:hover {
  background: rgba(var(--v-theme-on-surface), 0.22);
}

.chat-dock__action-btn:focus-visible {
  outline: 2px solid rgb(var(--v-theme-primary));
  outline-offset: 2px;
}

/* History panel — scrolls internally so the dock can grow upward while
   the composer stays anchored. In the expanded layout (with messages)
   it fills the available space between the action row and composer;
   min-height: 0 lets the flex child shrink so overflow-y: auto actually
   scrolls rather than bleeding past the viewport. */
.chat-dock__history {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 4px 2px;
}

.chat-bubble {
  max-width: 88%;
  padding: 8px 12px;
  border-radius: 14px;
  font-size: 0.92rem;
  line-height: 1.45;
  word-wrap: break-word;
}

.chat-bubble__text { white-space: pre-wrap; }

.chat-bubble__link {
  color: rgb(var(--v-theme-primary));
  text-decoration: underline;
  word-break: break-all;
}

/* Per-bubble TTS button — appears bottom-right inside assistant bubbles,
   semi-transparent until the bubble is hovered. Turns error-red while
   that bubble is the one currently being read out loud. */
.chat-bubble__tts {
  position: absolute;
  bottom: 4px;
  right: 4px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  padding: 0;
  border: 0;
  border-radius: 50%;
  background: rgba(var(--v-theme-on-surface), 0.08);
  color: rgba(var(--v-theme-on-surface), 0.65);
  opacity: 0;
  cursor: pointer;
  transition: opacity 120ms ease, background-color 120ms ease, color 120ms ease;
}

.chat-bubble:hover .chat-bubble__tts,
.chat-bubble__tts:focus-visible,
.chat-bubble__tts--on {
  opacity: 1;
}

.chat-bubble__tts:hover {
  background: rgba(var(--v-theme-on-surface), 0.18);
  color: rgb(var(--v-theme-on-surface));
}

.chat-bubble__tts--on {
  background: rgba(var(--v-theme-error), 0.14);
  color: rgb(var(--v-theme-error));
}

/* Give bubbles some right-padding so the text doesn't slide under the
   TTS button when one is present, and make them positioning contexts
   for the absolutely-positioned button. */
.chat-bubble--assistant {
  position: relative;
  padding-right: 36px;
}

.chat-bubble--user .chat-bubble__link {
  /* On the primary-tinted user bubble, primary-colour text would
     vanish against the bubble background. Invert to on-primary. */
  color: rgb(var(--v-theme-on-primary));
  text-decoration: underline;
}

.chat-bubble--user {
  align-self: flex-end;
  background: rgb(var(--v-theme-primary));
  color: rgb(var(--v-theme-on-primary));
  border-bottom-right-radius: 4px;
}

.chat-bubble--assistant {
  align-self: flex-start;
  background: rgba(var(--v-theme-on-surface), 0.08);
  border-bottom-left-radius: 4px;
}

.chat-bubble--error {
  align-self: flex-start;
  background: rgba(244, 67, 54, 0.12);
  color: rgb(var(--v-theme-error));
  border-bottom-left-radius: 4px;
}

.chat-bubble--typing {
  display: flex;
  align-items: center;
  gap: 8px;
  font-style: italic;
  opacity: 0.8;
}

.chat-suggestions {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  align-self: flex-start;
  max-width: 90%;
  margin-top: -2px;
  margin-bottom: 2px;
}

.chat-suggestion-chip {
  font: inherit;
  font-size: 0.78rem;
  line-height: 1.2;
  padding: 5px 10px;
  border: 1px solid rgba(var(--v-theme-on-surface), 0.18);
  border-radius: 999px;
  background: rgba(var(--v-theme-on-surface), 0.04);
  color: rgb(var(--v-theme-on-surface));
  cursor: pointer;
  transition: background-color 120ms ease, border-color 120ms ease;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
}

.chat-suggestion-chip:hover {
  background: rgba(var(--v-theme-primary), 0.12);
  border-color: rgba(var(--v-theme-primary), 0.5);
}

.chat-suggestion-chip:focus-visible {
  outline: 2px solid rgb(var(--v-theme-primary));
  outline-offset: 2px;
}

.chat-suggestion-chip:disabled {
  opacity: 0.5;
  cursor: default;
}

/* Alt-location chip — visually distinct from follow-up chips so the
 * user reads it as a correction ("I meant …") not another suggestion. */
.chat-suggestion-chip--alt {
  background: rgba(var(--v-theme-primary), 0.08);
  border-color: rgba(var(--v-theme-primary), 0.35);
  color: rgb(var(--v-theme-primary));
  font-weight: 500;
}

.chat-suggestion-chip--alt:hover {
  background: rgba(var(--v-theme-primary), 0.18);
}

.chat-cta-row {
  display: flex;
  align-self: flex-start;
  max-width: 90%;
  margin-top: -2px;
  margin-bottom: 2px;
}

/* Outlined style — solid primary would clash with user bubbles
 * (which are also solid primary). Transparent background + primary
 * ring reads as a call-to-action without colour-collision. */
.chat-cta-btn {
  font: inherit;
  font-size: 0.82rem;
  font-weight: 600;
  line-height: 1.2;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 13px;
  border: 1.5px solid rgb(var(--v-theme-primary));
  border-radius: 999px;
  background: transparent;
  color: rgb(var(--v-theme-primary));
  cursor: pointer;
  transition: background-color 120ms ease, color 120ms ease, transform 120ms ease;
}

.chat-cta-btn:hover {
  background: rgba(var(--v-theme-primary), 0.1);
}

.chat-cta-btn:active {
  transform: translateY(1px);
}

.chat-cta-btn:focus-visible {
  outline: 2px solid rgb(var(--v-theme-primary));
  outline-offset: 2px;
}

.chat-dock__rate {
  text-align: center;
  font-size: 0.76rem;
  color: rgba(var(--v-theme-on-surface), 0.6);
}

.chat-dock__pill {
  display: flex;
  align-items: flex-end;
  gap: 8px;
  padding: 4px 4px 4px 12px;
  background: rgba(var(--v-theme-on-surface), 0.05);
  color: rgb(var(--v-theme-on-surface));
  border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  border-radius: 22px;
  transition: border-color 120ms ease, background-color 120ms ease;
}

.chat-dock__pill:focus-within {
  border-color: rgb(var(--v-theme-primary));
  background: rgba(var(--v-theme-on-surface), 0.03);
}

.chat-dock__pill--loading { opacity: 0.8; }

.chat-dock__input {
  flex: 1;
  min-width: 0;
  min-height: 28px;
  max-height: 120px;
  padding: 4px 0;
  background: transparent;
  border: 0;
  outline: none;
  color: inherit;
  font-size: 16px;
  font-family: inherit;
  line-height: 1.35;
  resize: none;
  overflow-y: auto;
}

.chat-dock__input::placeholder {
  color: rgba(var(--v-theme-on-surface), 0.55);
}

.chat-dock__mic-error {
  margin-bottom: 4px;
  padding: 6px 10px;
  font-size: 0.8rem;
  line-height: 1.3;
  color: rgb(var(--v-theme-on-error));
  background: rgb(var(--v-theme-error));
  border-radius: 6px;
  text-align: left;
}

.chat-dock__send-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  padding: 0;
  border: 0;
  border-radius: 50%;
  background: rgb(var(--v-theme-primary));
  color: rgb(var(--v-theme-on-primary));
  cursor: pointer;
  flex-shrink: 0;
  align-self: flex-end;
  margin-bottom: 1px;
  transition: opacity 120ms ease, background-color 120ms ease;
}

.chat-dock__send-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.chat-dock__mic-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  padding: 0;
  border: 0;
  border-radius: 50%;
  background: transparent;
  color: rgba(var(--v-theme-on-surface), 0.65);
  cursor: pointer;
  flex-shrink: 0;
  align-self: flex-end;
  margin-bottom: 1px;
  transition: background-color 120ms ease, color 120ms ease;
}

.chat-dock__mic-btn:hover:not(:disabled) {
  background: rgba(var(--v-theme-on-surface), 0.08);
  color: rgb(var(--v-theme-on-surface));
}

.chat-dock__mic-btn:disabled {
  opacity: 0.35;
  cursor: not-allowed;
}

/* Active state when the mic is live — red dot + pulsing background so
   the user knows audio is being captured. */
.chat-dock__mic-btn--on {
  color: rgb(var(--v-theme-error));
  background: rgba(var(--v-theme-error), 0.1);
  animation: chat-mic-pulse 1.2s ease-in-out infinite;
}

@keyframes chat-mic-pulse {
  0%, 100% { background-color: rgba(var(--v-theme-error), 0.08); }
  50%      { background-color: rgba(var(--v-theme-error), 0.22); }
}
</style>
